import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import type { Role } from '../services/roleApi';
import { useAssignPermissions, useAvailablePermissions } from '../hooks/useRoles';
import { useConfirm } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface EditPermissionsDialogProps {
  role: Role;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const normalizePermissionValue = (value?: string | null) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const extractPermissionTokens = (permission: unknown): string[] => {
  if (typeof permission === 'string') {
    const normalized = normalizePermissionValue(permission);
    return normalized ? [normalized] : [];
  }

  if (permission && typeof permission === 'object') {
    const maybePermission = permission as Record<string, unknown>;
    const id = normalizePermissionValue(
      typeof maybePermission.id === 'string' ? maybePermission.id : undefined
    );
    const name = normalizePermissionValue(
      typeof maybePermission.name === 'string' ? maybePermission.name : undefined
    );
    return [id, name].filter(Boolean);
  }

  return [];
};

export const EditPermissionsDialog = ({ role, open, onOpenChange }: EditPermissionsDialogProps) => {
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const assignMutation = useAssignPermissions(role.id);
  const permissionsQuery = useAvailablePermissions();
  const confirm = useConfirm();

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearch('');
    onOpenChange(next);
  };

  const filteredPermissions = useMemo(() => {
    const all = permissionsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) => {
      const name = (p.name ?? '').toLowerCase();
      const description = (p.description ?? '').toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [permissionsQuery.data, search]);

  // Sync selected permissions from role whenever the dialog opens
  useEffect(() => {
    if (!open) return;

    const availablePermissions = permissionsQuery.data;

    if (!availablePermissions || availablePermissions.length === 0) {
      setSelectedPermissions([]);
      return;
    }

    const rolePermissionLookup = new Set(
      (role.permissions as unknown[]).flatMap(extractPermissionTokens)
    );

    const matchedPermissionNames = availablePermissions
      .filter((permission) => {
        const permissionId = normalizePermissionValue(permission.id);
        const permissionName = normalizePermissionValue(permission.name);

        return (
          rolePermissionLookup.has(permissionId) ||
          rolePermissionLookup.has(permissionName)
        );
      })
      .map((permission) => permission.name)
      .filter(Boolean);

    setSelectedPermissions(matchedPermissionNames);
  }, [open, role.permissions, permissionsQuery.data]);

  const handlePermissionToggle = (permissionName: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionName)
        ? prev.filter((p) => p !== permissionName)
        : [...prev, permissionName]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    confirm({
      title: "Update permissions?",
      description: `The “${role.name}” role will be set to ${selectedPermissions.length} permission(s). Users with this role are affected immediately.`,
      confirmLabel: "Update permissions",
      onConfirm: () =>
        assignMutation
          .mutateAsync({
            roleId: role.id,
            permissionNames: selectedPermissions,
          })
          .then(() => {
            toast.success(`Permissions updated for ${role.name}`);
            onOpenChange(false);
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Manage Permissions</DialogTitle>
          <DialogDescription>
            Configure permissions for {role.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search permissions by name or description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="space-y-3 border rounded-lg p-4 max-h-[55vh] overflow-y-auto">
            {filteredPermissions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No permissions match "{search}"
              </p>
            ) : (
              filteredPermissions.map((permission) => {
                const permissionId = permission.id || permission.name;
                const permissionName = permission.name || '';

                return (
                <div key={permissionId} className="flex items-center space-x-2">
                  <Checkbox
                    id={permissionId}
                    checked={selectedPermissions.includes(permissionName)}
                    onCheckedChange={() =>
                      permissionName && handlePermissionToggle(permissionName)
                    }
                    disabled={assignMutation.isPending}
                  />
                  <Label
                    htmlFor={permissionId}
                    className="cursor-pointer flex-1"
                  >
                    <div className="font-medium">{permissionName}</div>
                    <div className="text-xs text-gray-500">
                      {permission.description}
                    </div>
                  </Label>
                </div>
              )})
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Updating...' : 'Update Permissions'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};